import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MAX_DIM = 1600;
const inputCls = "bg-cream border-olive/40 focus-visible:ring-burgundy text-charcoal";

/* Downscale large photos client-side so uploads stay fast and small. */
async function prepareImage(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
    if (scale === 1 && file.size < 600 * 1024) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob) return file;
    return new File([blob.size < file.size ? blob : file], file.name.replace(/\.\w+$/, "") + ".jpg", {
      type: blob.size < file.size ? "image/jpeg" : file.type,
    });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const ImageUploadField = ({ label = "Image", value, onChange, testid = "image" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image is too large (max 15 MB).");
      return;
    }
    setUploading(true);
    try {
      const prepared = await prepareImage(file);
      const fd = new FormData();
      fd.append("file", prepared);
      const res = await api.post("/upload/image", fd);
      onChange(res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-charcoal/70">{label}</Label>
      <div className="mt-1.5 flex items-start gap-3">
        {value ? (
          <img
            src={value}
            alt=""
            data-testid={`${testid}-preview`}
            className="w-16 h-16 rounded object-cover border border-olive/40 bg-white flex-shrink-0"
          />
        ) : (
          <div className="w-16 h-16 rounded border border-dashed border-olive/40 grid place-items-center text-charcoal/30 flex-shrink-0">
            <ImageIcon size={20} />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            data-testid={`${testid}-upload-button`}
            className="rounded-none border-charcoal/40 text-charcoal w-full"
          >
            {uploading ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Upload size={16} className="mr-2" />
            )}
            {uploading ? "Uploading…" : "Upload image"}
          </Button>
          <Input
            data-testid={`${testid}-input`}
            className={inputCls}
            placeholder="…or paste an image URL"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testid}-file-input`}
      />
    </div>
  );
};
